import React, { useState } from 'react';
import {
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  MoreHorizontal,
  Clock,
  Hash,
  Calendar,
  Copy,
  Check,
  Download,
  Instagram,
  ShieldCheck,
  Video,
  Image as ImageIcon,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { ScheduledPost } from '@/lib/brandStudio';
import { activeBrand } from '@/lib/adminBrand';


type Step = 1 | 2 | 3 | 4 | 5 | 6;

interface ReadyToExportProps {
  posts: ScheduledPost[];
  fmtDateTime: (iso: string | null) => string;
  goToStep: (s: Step) => void;
}

const typeColors: Record<string, string> = {
  'Feed Post': 'bg-[#C9A961]/15 text-[#C9A961] border-[#C9A961]/30',
  Reel: 'bg-[#1A3B5C]/10 text-[#1A3B5C] border-[#1A3B5C]/30',
  Carousel: 'bg-[#8B6F47]/15 text-[#8B6F47] border-[#8B6F47]/30',
  Story: 'bg-rose-100 text-rose-700 border-rose-300',
};

// Download a remote media URL. Falls back to opening in a new tab if the
// cross-origin fetch is blocked.
async function downloadMedia(url: string, filename: string) {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const ReadyToExport: React.FC<ReadyToExportProps> = ({ posts, fmtDateTime, goToStep }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const approved = posts.filter((p) => p.status === 'approved' || p.approval_status === 'approved');

  const copy = (key: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDownload = async (post: ScheduledPost, kind: 'image' | 'video') => {
    const url = kind === 'video' ? post.video_url : post.image_url;
    if (!url) return;
    const key = `${post.id}-${kind}`;
    setDownloading(key);
    const ext = kind === 'video' ? 'mp4' : (url.split('.').pop()?.split('?')[0] || 'jpg');
    const safeDay = (post.day_label || 'post').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    await downloadMedia(url, `zera-${safeDay}-${post.id.slice(0, 6)}.${ext}`);
    setTimeout(() => setDownloading(null), 600);
  };

  if (approved.length === 0) {
    return (
      <div className="text-center py-16">
        <ShieldCheck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <h3 className="font-serif text-xl text-[#0D1B2A]">No Approved Posts Yet</h3>
        <p className="font-sans text-sm text-slate-500 mt-2 max-w-md mx-auto">
          Approve posts in the Post Editor (Step 3) to send them here. Approved posts become a read-only,
          download-ready library.
        </p>
        <button
          onClick={() => goToStep(3)}
          className="mt-5 font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]"
        >
          Go to Post Editor
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 5 · Ready Library</h3>
          <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
            Approved posts, locked and ready to publish. Nothing here can be edited — download any image or video,
            and copy captions and hashtags straight to your clipboard.
          </p>
        </div>
        <button
          onClick={() => goToStep(6)}
          className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center gap-2"
        >
          Analytics &amp; Connect <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 border-t border-b border-slate-200 py-5">
        {[
          { label: 'Approved Posts', value: String(approved.length) },
          { label: 'With Media', value: String(approved.filter((p) => p.image_url || p.video_url).length) },
          { label: 'Status', value: 'Locked · Ready' },
        ].map((s) => (
          <div key={s.label}>
            <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-1">{s.label}</div>
            <div className="font-serif text-xl text-[#1A3B5C]">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="space-y-16">
        {approved.map((post, index) => {
          const fullText = `${post.caption || ''}${post.cta ? `\n\n${post.cta}` : ''}${
            post.hashtags?.length ? `\n\n${post.hashtags.join(' ')}` : ''
          }`.trim();
          return (
            <div key={post.id} className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start border-b border-slate-100 pb-16 last:border-0">
              {/* Instagram mockup (read-only) */}
              <div className="lg:sticky lg:top-28">
                <div className="bg-white rounded-2xl shadow-xl shadow-[#1A3B5C]/10 overflow-hidden border border-[#C9A961]/20 max-w-[460px] mx-auto">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8B6F47] flex items-center justify-center">
                        <span className="font-serif text-white text-sm tracking-wider uppercase">{activeBrand().igHandle.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="font-sans text-[13px] font-semibold text-[#1A3B5C]">{activeBrand().igHandle}</div>
                        <div className="font-sans text-[10px] text-gray-500">{activeBrand().igLocation} · Sponsored</div>
                      </div>

                    </div>
                    <MoreHorizontal className="w-5 h-5 text-gray-500" />
                  </div>

                  <div className="relative aspect-square bg-gray-100">
                    {post.video_url ? (
                      <video src={post.video_url} className="w-full h-full object-cover" controls muted playsInline />
                    ) : post.image_url ? (
                      <img src={post.image_url} alt={post.theme || ''} className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-slate-300 font-sans text-xs">
                        No media
                      </div>
                    )}
                    {post.post_type === 'Reel' && (
                      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-md px-2 py-1 text-white text-[10px] tracking-wider font-sans">
                        REEL
                      </div>
                    )}
                  </div>

                  <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Heart className="w-6 h-6 text-gray-800" />
                      <MessageCircle className="w-6 h-6 text-gray-800" />
                      <Send className="w-6 h-6 text-gray-800" />
                    </div>
                    <Bookmark className="w-6 h-6 text-gray-800" />
                  </div>

                  <div className="px-4 pb-4">
                    <div className="font-sans text-[13px] font-semibold text-[#1A3B5C] mb-1">
                      {(1200 + index * 247).toLocaleString()} likes
                    </div>
                    <div className="font-sans text-[13px] text-gray-800 leading-relaxed whitespace-pre-line">
                      <span className="font-semibold text-[#1A3B5C]">{activeBrand().igHandle}</span>{' '}

                      {(post.caption || '').split('\n')[0]}
                      <button className="text-gray-500 ml-1">... more</button>
                    </div>
                    <div className="font-sans text-[10px] text-gray-400 tracking-wider mt-2 uppercase">
                      {fmtDateTime(post.scheduled_for)}
                    </div>
                  </div>
                </div>

                {/* Download buttons */}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                  {post.image_url && (
                    <button
                      onClick={() => handleDownload(post, 'image')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A3B5C] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors"
                    >
                      {downloading === `${post.id}-image` ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <ImageIcon className="w-3.5 h-3.5" />
                      )}
                      Download Image
                    </button>
                  )}
                  {post.video_url && (
                    <button
                      onClick={() => handleDownload(post, 'video')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A3B5C] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors"
                    >
                      {downloading === `${post.id}-video` ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Video className="w-3.5 h-3.5" />
                      )}
                      Download Video
                    </button>
                  )}
                  {!post.image_url && !post.video_url && (
                    <span className="font-sans text-[10px] tracking-wider uppercase text-slate-400">
                      No media to download
                    </span>
                  )}
                </div>
              </div>

              {/* Read-only details + copy */}
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3 border-b border-[#C9A961]/30 pb-4">
                  <div>
                    <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-2">
                      {post.theme || 'Approved Post'}
                    </div>
                    <h3 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] font-light">
                      {post.day_label || 'Post'}
                    </h3>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="px-2 py-1 border text-[9px] tracking-[0.2em] uppercase font-sans rounded-full bg-emerald-50 text-emerald-700 border-emerald-300 inline-flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Approved
                    </span>
                    <span
                      className={`px-3 py-1 border text-[10px] tracking-[0.2em] uppercase font-sans ${
                        typeColors[post.post_type] || typeColors['Feed Post']
                      }`}
                    >
                      {post.post_type}
                    </span>
                    <div className="flex items-center gap-3 text-[11px] text-[#1A3B5C]/60 font-sans">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fmtDateTime(post.scheduled_for)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3 flex items-center justify-between">
                    <span>Caption</span>
                    <button
                      onClick={() => copy(`${post.id}-cap`, post.caption || '')}
                      className="inline-flex items-center gap-1 text-[#1A3B5C] hover:text-[#C9A961] normal-case tracking-normal"
                    >
                      {copied === `${post.id}-cap` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied === `${post.id}-cap` ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <p className="font-sans text-[15px] text-[#1A3B5C]/85 leading-[1.8] whitespace-pre-line">
                    {post.caption || <span className="italic text-slate-400">No caption</span>}
                  </p>
                  {post.cta && (
                    <p className="font-sans text-[13px] text-[#8B6F47] italic mt-4 border-l-2 border-[#C9A961] pl-4">
                      CTA: {post.cta}
                    </p>
                  )}
                </div>

                {/* Hashtags */}
                {post.hashtags && post.hashtags.length > 0 && (
                  <div>
                    <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Hash className="w-3 h-3" /> Hashtags
                      </span>
                      <button
                        onClick={() => copy(`${post.id}-tags`, post.hashtags.join(' '))}
                        className="inline-flex items-center gap-1 text-[#1A3B5C] hover:text-[#C9A961] normal-case tracking-normal"
                      >
                        {copied === `${post.id}-tags` ? (
                          <Check className="w-3.5 h-3.5" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                        {copied === `${post.id}-tags` ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {post.hashtags.map((tag, i) => (
                        <span
                          key={`${tag}-${i}`}
                          className="font-sans text-[12px] text-[#1A3B5C] bg-[#F3EFE6] border border-[#C9A961]/30 px-3 py-1.5 rounded-full"
                        >
                          {tag.startsWith('#') ? tag : `#${tag}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Copy everything */}
                <button
                  onClick={() => copy(`${post.id}-all`, fullText)}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#C9A961] text-[#0D1B2A] font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#b89651] transition-colors"
                >
                  {copied === `${post.id}-all` ? (
                    <>
                      <Check className="w-4 h-4" /> Copied Full Post
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" /> Copy Caption + Hashtags
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReadyToExport;
